import { Routes } from '@angular/router';
import { DashboardComponent } from './feature/dashboard/dashboard.component';
import { authGuard, hasRoutePermission, loginGuard } from './core/services/auth.service';
import { ApiService } from './core/services/api.service';
import { inject } from '@angular/core';
import { ApiConstant } from './core/constants/api-constants';
import { environment } from '../environments/environment';
import { editorGuard } from './feature/json-editor/json-editor.service';
import { EnvironmentService } from '../environments/environment.service';
import { HelpDeskComponent } from './core/components/help-desk/help-desk';
import { tap } from 'rxjs';
import { StorageService } from './core/services/storage.service';
import { APP_CONSTANTS } from './core/constants/app-constants';
import { ApiMetaDataService } from './core/services/api-meta-data.service';

export const routes: Routes = [
  {
    path: '',
    component: DashboardComponent,
    resolve: {
      menu: () => inject(ApiService).ExecuteGet(environment.apiEndPoint + '/' + ApiConstant.AUTHENTICATION.MENU),
      locations: () => {
        const apiService = inject(ApiService);
        const storageService = inject(StorageService);
        const apiMetaDataService = inject(ApiMetaDataService)
        return apiService
          .ExecuteGet(`${environment.apiEndPoint}/${ApiConstant.LOCATION}`)
          .pipe(
            tap((res: any) => {
              const locations = res?.data;

              // only proceed if locations exist and are an array
              if (!Array.isArray(locations) || locations.length === 0) {
                return;
              }

              const profile = storageService.get('profile') || {};
              const hasGlob = profile?.locations?.some(
                (l: any) => l.loc_code === APP_CONSTANTS?.GLOBAL_LOCATION_TYPE
              );
              const globLocation = locations?.find(
                (l: any) => l.loc_code === APP_CONSTANTS.GLOBAL_LOCATION_TYPE
              );

              if (globLocation) {
                apiMetaDataService.setglobalLocation(globLocation);
              }

              if (hasGlob) {
                profile.locations = (locations || []).filter(
                  (l: any) => l.loc_code !== APP_CONSTANTS.GLOBAL_LOCATION_TYPE
                );
                storageService.set('profile', profile);
              }
            })
          );
      },
    },
    children: [
      {
        path: 'inventory',
        loadComponent: () => import('./feature/inventory/inventory/inventory.component').then(comp => comp.InventoryComponent),
        children: [
          {
            path: '',
            redirectTo: 'biological-sample-inventory/plasmids',
            pathMatch: 'full',
          },
          {
            path: '**',
            loadComponent: () => import('./feature/inventory/inventory-items/inventory-items.component').then(comp => comp.InventoryItemsComponent),
          },
        ],
        canActivate: [authGuard],
        canActivateChild: [hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.INVENTORY)],
      },
      {
        path: 'projects',
        loadComponent: () => import('./feature/project/project/project.component').then(comp => comp.ProjectComponent),
        children: [
          {
            path: '',
            loadComponent: () => import('./feature/project/project/project-dashboard/project-dashboard.component').then(comp => comp.ProjectDashboardComponent),
            // component: ProjectListingComponent,
          },
          {
            path: 'bsp-work-flow/:id/:exp_id',
            loadComponent: () => import('./feature/project/project/work-flow/work-flow.component').then(comp => comp.WorkFlowComponent),
            // component: WorkFlowComponent,
          },
          {
            path: 'pep-work-flow/:id/:exp_id',
            loadComponent: () => import('./feature/project/project/work-flow/work-flow.component').then(comp => comp.WorkFlowComponent),
            // component: WorkFlowComponent,
          },
          {
            path: 'app-work-flow/:id/:exp_id',
            loadComponent: () => import('./feature/project/project/antibody-project-workflow/antibody-project-workflow.component').then(comp => comp.AntibodyProjectWorkflowComponent),
            // component: AntibodyProjectWorkflowComponent,
          },
          {
            path: 'msp-work-flow/:id/:exp_id',
            loadComponent: () => import('./feature/project/project/mammalian-cell-project-workflow/mammalian-cell-project-workflow.component').then(comp => comp.MammalianCellProjectWorkflowComponent),
            // component: MammalianCellProjectWorkflowComponent,
          },

          // simsco exp workflow
          {
            path: 'ssDNA-to-dsDNA-work-flow/:project_id/:instance_id/:workflow_id',
            loadComponent: () => import('./feature/project/experiment-workflow/purified-ssdna-to-dsdna-workflow/purified-ssdna-to-dsdna-workflow.component').then(comp => comp.PurifiedSsdnaToDsdnaWorkflowComponent),
            canActivate: [hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.PROJECTS, APP_CONSTANTS.PROJECTS.SIMISCO_PROJECT_CODE.PURIFIEDssDNA)],
          },
          {
            path: 'electocompetent-strains-workflow/:project_id/:instance_id/:workflow_id',
            loadComponent: () => import('./feature/project/experiment-workflow/electocompetent-strains-workflow/electocompetent-strains-workflow.component').then(comp => comp.ElectocompetentStrainsWorkflowComponent),
            canActivate: [hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.PROJECTS, APP_CONSTANTS.PROJECTS.SIMISCO_PROJECT_CODE.ECS)],
          },
          {
            path: 'sep-workflow/:project_id/:instance_id/:workflow_id',
            loadComponent: () => import('./feature/project/experiment-workflow/sep-workflow/sep-workflow.component').then(comp => comp.SepWorkflowComponent),
            canActivate: [hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.PROJECTS, APP_CONSTANTS.PROJECTS.SIMISCO_PROJECT_CODE.SEP)],
          },
          {
            path: 'antigen-spec-antibody-workflow/:project_id/:instance_id/:workflow_id',
            loadComponent: () => import('./feature/project/experiment-workflow/antigen-spec-antibody-workflow/antigen-spec-antibody-workflow.component').then(comp => comp.AntigenSpecAntibodyWorkflowComponent),
            canActivate: [hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.PROJECTS, APP_CONSTANTS.PROJECTS.SIMISCO_PROJECT_CODE.SASA)],
          },
          {
            path: 'apc-elisa-workflow/:project_id/:instance_id/:workflow_id',
            loadComponent: () => import('./feature/project/experiment-workflow/apc-elisa-workflow/apc-elisa-workflow.component').then(comp => comp.ApcElisaWorkflowComponent),
            canActivate: [hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.PROJECTS, APP_CONSTANTS.PROJECTS.SIMISCO_PROJECT_CODE.APCE)],
          },
          {
            path: 'dna-sequencing-workflow/:project_id/:instance_id/:workflow_id',
            loadComponent: () => import('./feature/project/experiment-workflow/dna-sequencing-workflow/dna-sequencing-workflow.component').then(comp => comp.DnaSequencingWorkflowComponent),
            canActivate: [hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.PROJECTS, APP_CONSTANTS.PROJECTS.SIMISCO_PROJECT_CODE.DNASEQ)],
          },
          {
            path: 'construct-transfection-workflow/:project_id/:instance_id/:workflow_id',
            loadComponent: () => import('./feature/project/experiment-workflow/construct-transfection-workflow/construct-transfection-workflow.component').then(comp => comp.ConstructTransfectionWorkflowComponent),
            canActivate: [hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.PROJECTS, APP_CONSTANTS.PROJECTS.SIMISCO_PROJECT_CODE.CONSTRANS)],
          }
        ],
        canActivate: [authGuard, hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.PROJECTS)],
      },
      {
        path: 'protocols',
        loadComponent: () => import('./feature/protocols/protocols/protocols.component').then(comp => comp.ProtocolsComponent),
        data: { preload: "protocols" },
        canActivate: [authGuard, hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.PROTOCOL)],
      },

      {
        path: 'products',
        loadComponent: () => import('./feature/product/product.component').then(comp => comp.ProductComponent),
        data: { preload: "products" },
        canActivate: [authGuard],
      },

      {
        path: 'vendors',
        loadComponent: () => import('./feature/vendors/vendors/vendors.component').then(comp => comp.VendorsComponent),
        data: { preload: "vendors" },
        canActivate: [authGuard, hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.VENDOR)],
      },
      {
        path: 'vendors/:loc',
        loadComponent: () => import('./feature/vendors/vendors/vendors.component').then(comp => comp.VendorsComponent),
        data: { preload: "vendors" },
        canActivate: [authGuard, hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.VENDOR)],
      },
      //   path: 'vendors',
      //   loadComponent : ()=> import('./feature/vendors/vendors/vendors.component').then(comp=>comp.VendorsComponent),
      //   data: { preload: "vendors" },
      //   canActivate: [authGuard, permissionGuard],
      {
        path: 'settings',
        loadComponent: () => import('./feature/settings/settings/settings.component').then(comp => comp.SettingsComponent),
        data: { preload: "settings" },
        canActivate: [authGuard, hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.SETTINGS)],
      },
      {
        path: 'json-editor',
        loadComponent: () => import('./feature/json-editor/json-editor/json-editor.component').then(comp => comp.JsonEditorComponent),
        // component: SettingsComponent,
        canActivate: [authGuard, editorGuard],
      },
      {
        path: 'access-denied',
        loadComponent: () => import('./shared/components/access-denied/access-denied.component').then(comp => comp.AccessDeniedComponent),
        canActivate: [authGuard],
      },
      {
        path: 'study-list',
        loadComponent: () => import('./feature/inventory/study-list/study-list.component').then(comp => comp.StudyListComponent),
        // component: ProtocolsComponent,
        // canActivate: [authGuard, permissionGuard],
      },
      {
        path: 'lib-prj-listing',
        loadComponent: () => import('./feature/project/lib-temp-exp-listing/lib-temp-exp-listing.component').then(comp => comp.LibTempExpListingComponent),
        // component: ProtocolsComponent,
        // canActivate: [authGuard, permissionGuard],
      },
      {
        path: 'disease-program',
        loadComponent: () => import('./feature/disease-program/disease-program.component').then(comp => comp.DiseaseProgramComponent),
        // component: ProtocolsComponent,
        // canActivate: [authGuard, permissionGuard],
      },
      {
        path: 'experiments',
        loadComponent: () => import('./feature/experiments/experiments/experiments.component').then(comp => comp.ExperimentsComponent),
      },
      {
        path: 'manus-bio-experiments',
        loadComponent: () => import('./feature/manus-bio/experiments/experiments.component').then(comp => comp.ExperimentsComponent),
      },
      {
        path: 'help-desk',
        component: HelpDeskComponent,
        canActivate: [authGuard],
      },
      {
        path: '',
        redirectTo: () => {
          if (inject(EnvironmentService).clientId === '3') {
            return '/manus-bio-experiments';
          } else if (inject(EnvironmentService).clientId === '2') {
            return '/projects';
          }
          return 'inventory';
        },
        pathMatch: 'full',
      },
    ],
    canActivate: [authGuard],
  },
  {
    path: 'health',
    loadComponent: () => import('./feature/health/health').then(comp => comp.Health),
    // data: { preload: "protocols" },
    // canActivate: [authGuard, hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.PROTOCOL)],
  },
  {
    path: 'health-auth',
    loadComponent: () => import('./feature/health-auth/health-auth').then(comp => comp.HealthAuth),
    // data: { preload: "protocols" },
    // canActivate: [authGuard, hasRoutePermission(APP_CONSTANTS.PERMISSIONS.MENU.PROTOCOL)],
  },
  {
    path: 'login',
    loadComponent: () => import('./feature/account/login/login.component').then(comp => comp.LoginComponent),
    data: { preload: "login" },
    canActivate: [loginGuard],
  },
  {
    path: 'maintenance',
    loadComponent: () => import('./feature/account/maintenance/maintenance').then(comp => comp.MaintenanceComponent),

  },
  {
    path: 'forgot-password',
    loadComponent: () => import('./feature/account/forgot-password/forgot-password.component').then(comp => comp.ForgotPasswordComponent),
    data: { preload: "forgot-password" },
    canActivate: [loginGuard],
  },
  {
    path: 'reset-password/:token',
    loadComponent: () => import('./feature/account/reset-password/reset-password.component').then(comp => comp.ResetPasswordComponent),
    data: { preload: "reset-password" },
    canActivate: [loginGuard],
  },
  {
    path: 'activate-account/:token',
    loadComponent: () => import('./feature/account/account-activation/account-activation.component').then(comp => comp.AccountActivationComponent),
    data: { preload: "activate-account" },
    canActivate: [loginGuard],
  },

  {
    path: '**',
    redirectTo: 'login',
    data: { preload: "login" },
    pathMatch: 'full',
  },
];